import React, {
	FC,
	RefObject,
	useEffect,
	useRef,
	useState
} from 'react'
import {
	RadioList,
	RequestFormPDFProps,
	RequestInputFiles,
	RequestInputs
} from '@utils/request'
import requestForm from '@components/requestForms/request.form'
import { Collapse } from 'react-collapse'
import {
	REQUEST_TN_DETAILS_STEPS_NAME,
	requestCategories
} from '@services/constants'
import * as pageUtils from './__forms.utils'
import { useAppSelector } from '@services/store'
import TextInput from './inputs/text.inputs'
import { joinClasses, makeClasses } from '@utils/styles'
import RadioInput from '@components/requestForms/inputs/radio.input'
import RequestFiles from '@components/ui/requestFiles'
import Breakpoints from '@utils/breakpoints'

const SepticTankForm: FC<RequestFormPDFProps> = ({
	inputs,
	errors,
	subSteps,
	onSetInputFiles,
	filesInputs,
	isSummaryStep,
	toPrint,
	steps,
	onSetSubSteps,
	setCurrentStep,
	onFixError,
	onSetInputs
}) => {
	const {
		pageAssets,
		UpdateRequestInputsArray,
		UpdateRequestInputFilesArray,
		UpdateRequestInputsErrorsArray
	} = requestForm()

	type Classes = {
		inputContainer: string
		fieldsContainer: string
	}

	const classes: Classes = makeClasses({
		inputContainer: {
			marginBottom: '20px',
			display: 'block',
			width: '-webkit-fill-available'
		},
		fieldsContainer: {
			[Breakpoints.minWidth('md')]: {
				display: 'grid',
				columnGap: '20px',
				gridTemplateColumns: '1fr 1fr',
				alignItems: 'end'
			}
		}
	})

	const answers = useAppSelector((state) => state.request.configs)

	const [projectTypeInput, setProjectTypeInput] = useState<RequestInputs>({
		name: 'projectType',
		label: pageAssets?.request_form_septicTank_projectType,
		labelKey: 'request_form_septicTank_projectType',
		value: answers?.projectType?.toString() || '',
		required: true,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
	})

	const [propertyTypeInput, setPropertyTypeInput] = useState<RequestInputs>({
		name: 'propertyType',
		label: pageAssets?.request_form_septicTank_propertyType,
		labelKey: 'request_form_septicTank_propertyType',
		value: answers?.propertyType?.toString() || '',
		required: true,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
	})

	const [propertyBedroomsInput, setPropertyBedroomsInput] =
		useState<RequestInputs>({
			name: 'propertyBedrooms',
			label: pageAssets?.request_form_septicTank_propertyBedrooms,
			labelKey: 'request_form_septicTank_propertyBedrooms',
			value: answers?.propertyBedrooms?.toString() || '',
			required: answers?.propertyType === 293052000,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		})

	const [propertyUnitsInput, setPropertyUnitsInput] = useState<RequestInputs>({
		name: 'propertyUnits',
		label: pageAssets?.request_form_septicTank_propertyUnits,
		labelKey: 'request_form_septicTank_propertyUnits',
		value: answers?.propertyUnits?.toString() || '',
		required: answers?.propertyType === 293052000,
		ref: useRef<HTMLInputElement>(null),
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
	})

	const [propertyFlowRateInput, setPropertyFlowRateInput] =
		useState<RequestInputs>({
			name: 'propertyFlowRate',
			label: pageAssets?.request_form_septicTank_propertyFlowRate,
			labelKey: 'request_form_septicTank_propertyFlowRate',
			value: answers?.propertyFlowRate?.toString() || '',
			required: answers?.propertyType === 293052001,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		})

	const [propertyWaterSourceInput, setPropertyWaterSourceInput] =
		useState<RequestInputs>({
			name: 'propertyWaterSource',
			label: pageAssets?.request_form_septicTank_propertyWaterSource,
			labelKey: 'request_form_septicTank_propertyWaterSource',
			value: answers?.propertyWaterSource?.toString() || '',
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		})

	const radioListProjectType: RadioList[] = [
		{
			label: pageAssets?.request_form_septicTank_projectType_option1,
			value: '293051000',
			key: 'request_form_septicTank_projectType_option1'
		},
		{
			label: pageAssets?.request_form_septicTank_projectType_option2,
			value: '293051001',
			key: 'request_form_septicTank_projectType_option2'
		},
		{
			label: pageAssets?.request_form_septicTank_projectType_option3,
			value: '293051002',
			key: 'request_form_septicTank_projectType_option3'
		}
	]

	const radioListPropertyType: RadioList[] = [
		{
			label: pageAssets?.request_form_septicTank_propertyType_option1,
			value: '293052000',
			key: 'request_form_septicTank_propertyType_option1'
		},
		{
			label: pageAssets?.request_form_septicTank_propertyType_option2,
			value: '293052001',
			key: 'request_form_septicTank_propertyType_option2'
		}
	]

	const radioListWaterType: RadioList[] = [
		{
			label: pageAssets?.request_form_septicTank_waterType_option1,
			value: '293053000',
			key: 'request_form_septicTank_waterType_option1'
		},
		{
			label: pageAssets?.request_form_septicTank_waterType_option2,
			value: '293053001',
			key: 'request_form_septicTank_waterType_option2'
		},
		{
			label: pageAssets?.request_form_septicTank_waterType_option3,
			value: '293053002',
			key: 'request_form_septicTank_waterType_option3'
		},
		{
			label: pageAssets?.request_form_septicTank_waterType_option4,
			value: '293053003',
			key: 'request_form_septicTank_waterType_option4'
		}
	]

	const [usageFeeBill, setUsageFeeBill] = useState<RequestInputFiles>({
		name: 'usageFeeBill',
		label: pageAssets?.request_form_septicTank_make_document_label,
		labelKey: 'request_form_septicTank_make_document_label',
		description: '',
		files: new Set<File>(
			filesInputs?.find((el) => el.name == 'usageFeeBill')?.files || []
		),
		required: true,
		subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
		shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
		numberFilesRequired: 1,
		numberMaxFiles: 10,
		category: requestCategories.proofOfPurchase
	})

	const [proofOfResidencyInput, setproofOfResidencyInput] =
		useState<RequestInputFiles>({
			name: 'proofOfResidencyInput',
			label: pageAssets?.request_form_septicTank_certificate_label,
			labelKey: 'request_form_septicTank_certificate_label',
			description: '',
			files: new Set<File>(
				filesInputs?.find((el) => el.name == 'proofOfResidencyInput')?.files ||
					[]
			),
			required: false,
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
			shouldValidateIn: REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS,
			numberFilesRequired: 1,
			category: requestCategories.proofOfResidency
		})

	const onSelectProjectTypeInput = (value) => {
		setProjectTypeInput({
			...projectTypeInput,
			value: value ? `${value}` : ''
		})

		onFixError(errors.filter((error) => error !== projectTypeInput.name))
	}

	const onSelectPropertyTypeInput = (value) => {
		setPropertyTypeInput({
			...propertyTypeInput,
			value: value ? `${value}` : ''
		})

		setPropertyBedroomsInput({
			...propertyBedroomsInput,
			required: value == '293052000',
			value: ''
		})

		setPropertyUnitsInput({
			...propertyUnitsInput,
			required: value == '293052000',
			value: ''
		})

		setPropertyFlowRateInput({
			...propertyFlowRateInput,
			required: value == '293052001',
			value: ''
		})

		onFixError(
			errors.filter(
				(error) =>
					![
						propertyTypeInput.name,
						propertyBedroomsInput.name,
						propertyUnitsInput.name,
						propertyFlowRateInput.name
					].includes(error)
			)
		)
	}

	const onSelectPropertyBedroomsInput = (value) => {
		if (value !== '' && Number(value) < 0) {
			return
		}
		setPropertyBedroomsInput({
			...propertyBedroomsInput,
			value: value ? `${value}` : ''
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, propertyBedroomsInput.name)
		)
	}

	const onSelectPropertyUnitsInput = (value) => {
		if (value !== '' && Number(value) < 0) {
			return
		}

		setPropertyUnitsInput({
			...propertyUnitsInput,
			value: value ? `${value}` : ''
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, propertyUnitsInput.name))
	}

	const onSelectPropertyFlowRateInput = (value) => {
		if (value !== '' && Number(value) < 0) {
			return
		}

		setPropertyFlowRateInput({
			...propertyFlowRateInput,
			value: value ? `${value}` : ''
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, propertyFlowRateInput.name)
		)
	}

	const onSelectPropertyWaterSourceInput = (value) => {
		setPropertyWaterSourceInput({
			...propertyWaterSourceInput,
			value: value ? `${value}` : ''
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, propertyWaterSourceInput.name)
		)
	}

	const onSelectUsageFeeBill = (files: Set<File>) => {
		setUsageFeeBill({
			...usageFeeBill,
			files
		})

		onFixError(UpdateRequestInputsErrorsArray(errors, usageFeeBill.name))
	}

	const onSelectProofOfResidency = (files: Set<File>) => {
		setproofOfResidencyInput({
			...proofOfResidencyInput,
			files
		})

		onFixError(
			UpdateRequestInputsErrorsArray(errors, proofOfResidencyInput.name)
		)
	}

	const onSetErrorFromFileComponent = (
		hasErrorInComponent: boolean,
		inputName: string
	) => {
		if (hasErrorInComponent) {
			if (!errors.includes(inputName)) {
				onFixError((oldErrors) => [...oldErrors, inputName])
			}
			return
		}

		onFixError(UpdateRequestInputsErrorsArray(errors, inputName))
	}

	const backToForm = (step: number, subStep: string) => {
		if (setCurrentStep && onSetSubSteps && subSteps) {
			setCurrentStep(step)
			onSetSubSteps({
				...subSteps,
				position: subStep as REQUEST_TN_DETAILS_STEPS_NAME
			})
		}
	}

	const questionAnswer = (question: string, answer?: string) => {
		if (answer) {
			return (
				<div>
					<strong>{question}</strong>
					<span
						className={joinClasses([pageUtils.classes.answerURB, 'answerURB'])}
					>
						{answer}
					</span>
				</div>
			)
		}
	}

	const getLabelFromList = (list, value) => {
		return list?.find((element) => element.value == value?.toString())?.label
	}

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, projectTypeInput))
	}, [projectTypeInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, propertyTypeInput))
	}, [propertyTypeInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, propertyBedroomsInput))
	}, [propertyBedroomsInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, propertyUnitsInput))
	}, [propertyUnitsInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, propertyFlowRateInput))
	}, [propertyFlowRateInput])

	useEffect(() => {
		onSetInputs(UpdateRequestInputsArray(inputs, propertyWaterSourceInput))
	}, [propertyWaterSourceInput])

	// DOCUMENTS

	useEffect(() => {
		onSetInputFiles((prev) => UpdateRequestInputFilesArray(prev, usageFeeBill))
	}, [usageFeeBill])

	useEffect(() => {
		onSetInputFiles((prev) =>
			UpdateRequestInputFilesArray(prev, proofOfResidencyInput)
		)
	}, [proofOfResidencyInput])

	useEffect(() => {
		onSetSubSteps({
			map: true,
			details_1: true,
			details_2: false,
			requiredDocument: true,
			position:
				subSteps?.position && String(subSteps?.position) !== ''
					? subSteps?.position
					: REQUEST_TN_DETAILS_STEPS_NAME.MAP,
			steps: [
				REQUEST_TN_DETAILS_STEPS_NAME.MAP,
				REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1,
				REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
			]
		})
	}, [])

	if (isSummaryStep) {
		return (
			<section
				className={pageUtils.classes.noMargin}
				style={{ padding: '0px' }}
			>
				{subSteps?.details_1 && (
					<>
						<div>
							<a
								style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
								className={joinClasses([
									pageUtils.classes.btn,
									pageUtils.classes.button,
									pageUtils.classes.buttonOutline
								])}
								onClick={() =>
									backToForm(
										steps?.form!,
										REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
									)
								}
							>
								{pageAssets?.label_modify}
							</a>
							<h4 className={pageUtils.classes.h4}>
								{pageAssets?.request_form_septicTank_firstSubStep_title}
							</h4>

							{questionAnswer(
								projectTypeInput.label,
								getLabelFromList(radioListProjectType, answers?.projectType)
							)}

							{questionAnswer(
								propertyTypeInput.label,
								getLabelFromList(radioListPropertyType, answers?.propertyType)
							)}

							{questionAnswer(
								propertyBedroomsInput.label,
								answers?.propertyBedrooms?.toString()
							)}

							{questionAnswer(
								propertyUnitsInput.label,
								answers?.propertyUnits?.toString()
							)}

							{questionAnswer(
								propertyFlowRateInput.label,
								answers?.propertyFlowRate?.toString()
							)}

							{questionAnswer(
								propertyWaterSourceInput.label,
								getLabelFromList(
									radioListWaterType,
									answers?.propertyWaterSource
								)
							)}
						</div>
					</>
				)}

				{subSteps?.requiredDocument && (
					<>
						<a
							style={{ display: toPrint ? 'none' : 'flex', float: 'right' }}
							className={joinClasses([
								pageUtils.classes.btn,
								pageUtils.classes.button,
								pageUtils.classes.buttonOutline
							])}
							onClick={() =>
								backToForm(
									steps?.form!,
									REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
								)
							}
						>
							{pageAssets?.label_modify}
						</a>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.page_requestInfo_enclosed}
						</h4>

						<RequestFiles
							subtitle={usageFeeBill.label}
							name={usageFeeBill.name}
							required
							files={usageFeeBill.files}
							hasError={errors.includes(usageFeeBill.name)}
							subSteps={subSteps}
							onSetHasError={onSetErrorFromFileComponent}
							onSetFiles={onSelectUsageFeeBill}
							maxFiles={usageFeeBill.numberMaxFiles}
							description={usageFeeBill.description}
							isSummary
						>
							<></>
						</RequestFiles>

						<RequestFiles
							subtitle={proofOfResidencyInput.label}
							name={proofOfResidencyInput.name}
							required
							files={proofOfResidencyInput.files}
							hasError={errors.includes(proofOfResidencyInput.name)}
							subSteps={subSteps}
							onSetHasError={onSetErrorFromFileComponent}
							onSetFiles={onSelectProofOfResidency}
							maxFiles={proofOfResidencyInput.numberMaxFiles}
							description={proofOfResidencyInput.description}
							isSummary
						>
							<></>
						</RequestFiles>
					</>
				)}
			</section>
		)
	}

	return (
		<>
			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1 && (
				<Collapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
					}
				>
					<section>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.request_form_septicTank_firstSubStep_title}
						</h4>

						<RadioInput
							name={projectTypeInput.name}
							label={projectTypeInput.label}
							value={projectTypeInput.value}
							list={radioListProjectType}
							onChange={onSelectProjectTypeInput}
							hasError={errors.includes(projectTypeInput.name)}
							required={true}
							ref={projectTypeInput.ref as RefObject<HTMLInputElement>}
							hasBackgroundWrapper
						/>

						<RadioInput
							name={propertyTypeInput.name}
							label={propertyTypeInput.label}
							value={propertyTypeInput.value}
							list={radioListPropertyType}
							onChange={onSelectPropertyTypeInput}
							hasError={errors.includes(propertyTypeInput.name)}
							required={true}
							ref={propertyTypeInput.ref as RefObject<HTMLInputElement>}
							hasBackgroundWrapper
						/>

						{propertyTypeInput.value === '293052000' && (
							<>
								<TextInput
									id={propertyBedroomsInput.name}
									label={propertyBedroomsInput.label}
									type="number"
									value={propertyBedroomsInput.value}
									onChange={onSelectPropertyBedroomsInput}
									hasError={errors.includes(propertyBedroomsInput.name)}
									required
									ref={propertyBedroomsInput.ref as RefObject<HTMLInputElement>}
								/>

								<TextInput
									id={propertyUnitsInput.name}
									label={propertyUnitsInput.label}
									type="number"
									value={propertyUnitsInput.value}
									onChange={onSelectPropertyUnitsInput}
									hasError={errors.includes(propertyUnitsInput.name)}
									required
									ref={propertyUnitsInput.ref as RefObject<HTMLInputElement>}
								/>
							</>
						)}

						{propertyTypeInput.value === '293052001' && (
							<>
								<TextInput
									id={propertyFlowRateInput.name}
									label={propertyFlowRateInput.label}
									type="number"
									value={propertyFlowRateInput.value}
									onChange={onSelectPropertyFlowRateInput}
									hasError={errors.includes(propertyFlowRateInput.name)}
									required
									ref={propertyFlowRateInput.ref as RefObject<HTMLInputElement>}
								/>
							</>
						)}

						<RadioInput
							name={propertyWaterSourceInput.name}
							label={propertyWaterSourceInput.label}
							value={propertyWaterSourceInput.value}
							list={radioListWaterType}
							onChange={onSelectPropertyWaterSourceInput}
							hasError={errors.includes(propertyWaterSourceInput.name)}
							required={true}
							ref={propertyWaterSourceInput.ref as RefObject<HTMLInputElement>}
							hasBackgroundWrapper
						/>
					</section>
				</Collapse>
			)}
			{subSteps?.position === REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS && (
				<Collapse
					isOpened={
						subSteps !== undefined &&
						subSteps.position === REQUEST_TN_DETAILS_STEPS_NAME.DOCUMENTS
					}
				>
					<section>
						<h4 className={pageUtils.classes.h4}>
							{pageAssets?.page_requestInfo_enclosed}
						</h4>

						<RequestFiles
							subtitle={usageFeeBill.label}
							name={usageFeeBill.name}
							required
							files={usageFeeBill.files}
							hasError={errors.includes(usageFeeBill.name)}
							subSteps={subSteps}
							onSetHasError={onSetErrorFromFileComponent}
							onSetFiles={onSelectUsageFeeBill}
							maxFiles={usageFeeBill.numberMaxFiles}
							description={usageFeeBill.description}
							removePadding
						></RequestFiles>

						<RequestFiles
							subtitle={proofOfResidencyInput.label}
							name={proofOfResidencyInput.name}
							files={proofOfResidencyInput.files}
							hasError={errors.includes(proofOfResidencyInput.name)}
							subSteps={subSteps}
							onSetHasError={onSetErrorFromFileComponent}
							onSetFiles={onSelectProofOfResidency}
							maxFiles={proofOfResidencyInput.numberMaxFiles}
							description={proofOfResidencyInput.description}
							removePadding
						></RequestFiles>
					</section>
				</Collapse>
			)}
		</>
	)
}

export default SepticTankForm
